<template>
    <properties-find
        :uuid="$route.params.property"
        :include="[
            'address',
            'documents(paginate:1,6|include:files(paginate:1,1))',
            'maintenances(paginate:1,5)',
            'note',
            'tasks(paginate:1,5)',
            'tenancies(paginate:1,3|include:residencies(paginate:1,5|include:occupant))'
        ]"
    >
        <div
            slot-scope="{
                sid,
                data: property,
                error,
                is_loading,
                has_loaded,
                find
            }"
        >
            <template v-if="!has_loaded || error">
                <base-loading
                    :error="error"
                    :retry="find"
                    class="py-6"
                />

                <error-modal
                    v-if="error"
                    :error="error"
                />
            </template>

            <template v-else>
                <base-hint name="landlords-properties-show">
                    <template slot="heading">
                        Viewing a property
                    </template>

                    <p class="mb-3">
                        You're currently viewing the property {{ property.name }}.
                    </p>

                    <p class="mb-3">
                        As you'll see on this page - properties have listings, tenancies and documents associated with them.
                    </p>

                    <button
                        class="w-full btn btn-sm btn-grey-darker md:w-auto"
                        @click.prevent="introjs"
                    >
                        What's on this page?
                    </button>
                </base-hint>

                <base-alert
                    v-if="property.archived_at"
                    level="warning"
                    class="mb-6"
                >
                    <template slot="heading">Property Archived</template>

                    <template slot="content">
                        <p>This property was archived on {{ property.archived_at | moment('D MMM, YYYY') }}.</p>
                    </template>
                </base-alert>

                <details-card class="mb-6 id-details-card">
                    <template slot="header">
                        <div class="flex-grow mr-3 overflow-hidden">
                            <h1 class="text-lg font-normal truncate">
                                {{ property.name }}
                            </h1>
                        </div>

                        <div
                            v-if="is_loading"
                            class="w-6 h-6 mr-3"
                        >
                            <base-svg
                                name="icon-loading"
                                class="w-full h-full"
                            />
                        </div>

                        <edit-dropdown
                            v-if="!property.archived_at"
                            :property="property"
                            class="flex-none id-edit-dropdown"
                            @success="find"
                        />
                    </template>
                    <template slot="body">
                        <grid-layout-item class="mb-3 sm:w-1/2 lg:w-1/3">
                            <div class="text-xs text-label">Address</div>
                            <div class="text-sm">
                                <base-address :address="property.address" />
                            </div>
                        </grid-layout-item>
                    </template>
                </details-card>

                <tasks-card
                    :data="property.tasks.data"
                    :meta="property.tasks.meta"
                    :can-new="true"
                    :new-to="{
                        name: $router.fix('dashboard.*.tasks.create'),
                        query: {
                            property: property.uuid
                        }
                    }"
                    :more-to="{
                        name: $router.fix('dashboard.*.tasks.index'),
                        query: {
                            property: property.uuid
                        }
                    }"
                    class="mb-6 id-tasks-card"
                    @success="find"
                />

                <notes-card
                    v-if="$gate.role('landlord')"
                    :note="property.note"
                    :notable="property"
                    class="mb-6"
                />

                <tenancies-card
                    :data="property.tenancies.data"
                    :meta="property.tenancies.meta"
                    :can-new="!property.archived_at"
                    :new-to="{
                        name: $router.fix('dashboard.*.tenancies.create'),
                        query: {
                            property: property.uuid
                        }
                    }"
                    :more-to="{
                        name: $router.fix('dashboard.*.tenancies.index'),
                        query: {
                            property: property.uuid
                        }
                    }"
                    class="mb-6 id-tenancies-card"
                >
                    <truncate-hack
                        slot="primary"
                        slot-scope="{ tenancy }"
                    >
                        <template v-if="$gate.role('landlord')">
                            <template v-if="tenancy.residencies && tenancy.residencies.meta.count > 0">
                                {{ tenancy.residencies.data | comma-delimited({
                                    callback: (residency) => residency.occupant.name,
                                    total: tenancy.residencies.meta.total
                                }) }}
                            </template>
                            <em
                                v-else
                                class="italic text-grey-darker"
                            >
                                No tenants
                            </em>
                        </template>
                        <template v-else>
                            {{ tenancy.property.landlord.name }}
                        </template>
                    </truncate-hack>
                </tenancies-card>

                <maintenances-card
                    v-if="_.preload('app.beta.is-enrolled')"
                    :data="property.maintenances.data"
                    :meta="property.maintenances.meta"
                    :can-new="true"
                    :new-to="{
                        name: $router.fix('dashboard.*.maintenances.create'),
                        query: {
                            property: property.uuid
                        }
                    }"
                    :more-to="{
                        name: $router.fix('dashboard.*.maintenances.index'),
                        query: {
                            property: property.uuid
                        }
                    }"
                    class="mb-6 id-maintenances-card"
                />

                <documents-card
                    :data="property.documents.data"
                    :meta="property.documents.meta"
                    :can-new="true"
                    :new-to="{
                        name: $router.fix('dashboard.*.documents.create'),
                        query: {
                            property: property.uuid
                        }
                    }"
                    :more-to="{
                        name: $router.fix('dashboard.*.documents.index'),
                        query: {
                            property: property.uuid
                        }
                    }"
                    class="mb-6 id-documents-card"
                />
            </template>
        </div>
    </properties-find>
</template>

<script>
    import scroll from '../../../../support/scroll'
    import DetailsCard from '../../../../components/details-card'
    import DocumentsCard from '../../../../components/documents-card'
    import EditDropdown from './show/edit-dropdown'
    import MaintenancesCard from '../../../../components/maintenances-card'
    import NotesCard from '../../../../components/notes-card'
    import PropertiesFind from '../../../../components/properties-find'
    import TasksCard from '../../../../components/tasks-card'
    import TenanciesCard from '../../../../components/tenancies-card'

    export default {
        components: {
            DetailsCard,
            DocumentsCard,
            EditDropdown,
            MaintenancesCard,
            NotesCard,
            PropertiesFind,
            TasksCard,
            TenanciesCard
        },
        beforeRouteLeave (to, from, next) {
            this.$introjs.exit()

            next()
        },
        created () {
            this.$eventBus.$on('the-layout-dashboard:is_sidebar_open', () => {
                this.$introjs.exit()
            })
        },
        methods: {
            introjs () {
                this.$introjs.start([
                    {
                        intro:
                            '<p class="mb-3">The property details are shown here.</p>' +
                            '<p>More options are available from the <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="inline-block w-5 h-5 icon-dots-horizontal"> <path fill-rule="evenodd" clip-rule="evenodd" d="M5 14C4.46957 14 3.96086 13.7893 3.58579 13.4142C3.21071 13.0391 3 12.5304 3 12C3 11.4696 3.21071 10.9609 3.58579 10.5858C3.96086 10.2107 4.46957 10 5 10C5.53043 10 6.03914 10.2107 6.41421 10.5858C6.78929 10.9609 7 11.4696 7 12C7 12.5304 6.78929 13.0391 6.41421 13.4142C6.03914 13.7893 5.53043 14 5 14ZM12 14C11.4696 14 10.9609 13.7893 10.5858 13.4142C10.2107 13.0391 10 12.5304 10 12C10 11.4696 10.2107 10.9609 10.5858 10.5858C10.9609 10.2107 11.4696 10 12 10C12.5304 10 13.0391 10.2107 13.4142 10.5858C13.7893 10.9609 14 11.4696 14 12C14 12.5304 13.7893 13.0391 13.4142 13.4142C13.0391 13.7893 12.5304 14 12 14ZM19 14C18.4696 14 17.9609 13.7893 17.5858 13.4142C17.2107 13.0391 17 12.5304 17 12C17 11.4696 17.2107 10.9609 17.5858 10.5858C17.9609 10.2107 18.4696 10 19 10C19.5304 10 20.0391 10.2107 20.4142 10.5858C20.7893 10.9609 21 11.4696 21 12C21 12.5304 20.7893 13.0391 20.4142 13.4142C20.0391 13.7893 19.5304 14 19 14Z" class="primary"></path></svg> menu, for example, editing the property.</p>',
                        element: '.id-details-card',
                        position: 'bottom'
                    },
                    {
                        intro:
                            '<p class="mb-3">Tasks will keep you on track with what to do next with this property.</p>' +
                            '<p class="text-sm">p.s. You can click on tasks.</p>',
                        element: '.id-tasks-card'
                    },
                    {
                        intro: '<p>Tenancies related to this property - both past and present - will be listed here.</p>',
                        element: '.id-tenancies-card'
                    },
                    {
                        intro:
                            '<p class="mb-3">Upload documents related to this property for safe keeping, for example gas certificates.</p>' +
                            '<p class="text-sm">Tip: You can easily opt to share specific documents with your tenants.</p>',
                        element: '.id-documents-card'
                    }
                ])
                    .oncomplete(() => {
                        scroll.toTop()
                    })
            }
        }
    }
</script>
